import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations } from "./translations";

const STORAGE_KEY = "driftex_lang";
const COOKIE_KEY = "NEXT_LOCALE";
const SUPPORTED = ["cz", "en"];

const LanguageContext = createContext(null);

/* ---------------- helpers ---------------- */

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const persistLang = (lang) => {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // storage unavailable
  }
  document.cookie = `${COOKIE_KEY}=${lang};path=/;max-age=31536000;SameSite=Lax`;
};

const getSavedLang = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED.includes(stored)) return stored;
  } catch {
    // storage unavailable
  }
  const cookie = readCookie(COOKIE_KEY);
  if (SUPPORTED.includes(cookie)) return cookie;
  return null;
};

// Browser exposes the Accept-Language preferences via navigator.languages
const detectBrowserLang = () => {
  const prefs = navigator.languages || [navigator.language || ""];
  for (const p of prefs) {
    const code = (p || "").toLowerCase();
    if (code.startsWith("en")) return "en";
    if (code.startsWith("cs") || code.startsWith("sk")) return "cz";
  }
  return "cz"; // fallback to Czech
};

const langFromPath = (pathname) =>
  pathname === "/en" || pathname.startsWith("/en/") ? "en" : "cz";

const stripEnPrefix = (pathname) => {
  if (pathname === "/en" || pathname === "/en/") return "/";
  if (pathname.startsWith("/en/")) return pathname.slice(3);
  return pathname;
};

const resolveKey = (dict, path) =>
  path.split(".").reduce((acc, k) => (acc == null ? undefined : acc[k]), dict);

const upsertLinkTag = (id, rel, hreflang, href) => {
  let el = document.head.querySelector(`link[data-i18n="${id}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("data-i18n", id);
    document.head.appendChild(el);
  }
  el.setAttribute("rel", rel);
  if (hreflang) el.setAttribute("hreflang", hreflang);
  el.setAttribute("href", href);
};

/* ---------------- provider ---------------- */

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const didInit = useRef(false);
  const [lang, setLangState] = useState(() => langFromPath(window.location.pathname));

  // Init + keep language in sync with the URL (URL is source of truth after init)
  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      if (langFromPath(location.pathname) === "en") {
        setLangState("en");
        return;
      }
      // no /en prefix -> saved preference, else browser Accept-Language, else cz
      const preferred = getSavedLang() || detectBrowserLang();
      if (preferred === "en") {
        navigate(`/en${location.pathname}${location.search}`, { replace: true });
      }
      return;
    }
    setLangState(langFromPath(location.pathname));
  }, [location, navigate]);

  // SEO side effects: <html lang>, title, hreflang alternates
  useEffect(() => {
    const basePath = stripEnPrefix(location.pathname);
    const origin = window.location.origin;
    document.documentElement.lang = lang === "en" ? "en" : "cs";
    document.title = translations[lang].meta.title;
    upsertLinkTag("alt-cs", "alternate", "cs", `${origin}${basePath}`);
    upsertLinkTag("alt-en", "alternate", "en", `${origin}${basePath === "/" ? "/en" : `/en${basePath}`}`);
    upsertLinkTag("alt-default", "alternate", "x-default", `${origin}${basePath}`);
    upsertLinkTag("canonical", "canonical", null, `${origin}${location.pathname}`);
  }, [lang, location.pathname]);

  // Manual switch: persist choice + move to the equivalent localized path
  const switchLang = useCallback(
    (next) => {
      if (!SUPPORTED.includes(next) || next === lang) return;
      persistLang(next);
      const basePath = stripEnPrefix(location.pathname);
      const target = next === "en" ? (basePath === "/" ? "/en" : `/en${basePath}`) : basePath;
      navigate(`${target}${location.search}`);
    },
    [lang, location, navigate]
  );

  // t("dot.path") -> localized string/array/object, falls back to CZ
  const t = useCallback(
    (path) => {
      const val = resolveKey(translations[lang], path);
      if (val !== undefined) return val;
      const fallback = resolveKey(translations.cz, path);
      return fallback !== undefined ? fallback : path;
    },
    [lang]
  );

  // localePath("/dilna") -> "/dilna" | "/en/dilna"
  const lp = useCallback(
    (path) => (lang === "en" ? (path === "/" ? "/en" : `/en${path}`) : path),
    [lang]
  );

  // pick localized field from a DB document: pickField(doc, "name") -> name_en || name
  const pickField = useCallback(
    (doc, field) => (lang === "en" ? doc?.[`${field}_en`] || doc?.[field] : doc?.[field]),
    [lang]
  );

  const dateLocale = lang === "en" ? "en-GB" : "cs-CZ";

  const value = useMemo(
    () => ({ lang, switchLang, t, lp, pickField, dateLocale }),
    [lang, switchLang, t, lp, pickField, dateLocale]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
